/* eslint-disable react/prop-types */
/**
 * /settings/platform — admin/owner only.
 *
 * Tenant-level admin layer: Google integrations, Industry taxonomy,
 * Doc QA, Data quality, Bulletin manager, Data migration (owner+admin),
 * Feature flags (owner), Triage console (owner).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, CheckCircle2, X, AlertTriangle, Activity } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/TenantBrandingContext";
import { EntityShell } from "../../components/shell";
import IndustryTaxonomyCard from "../../components/IndustryTaxonomyCard";
import BulletinManagerCard from "../../components/BulletinManagerCard";
import DocQACard from "../../components/DocQACard";
import RogueModulesCard from "../../components/RogueModulesCard";
import UnresolvedWidget from "../../components/dashboard/UnresolvedWidget";
import TenantDataCleanupCard from "../../components/TenantDataCleanupCard";
import GalleryReconcileCard from "../../components/GalleryReconcileCard";
import {
  DataMigrationBlock,
  FeatureFlagsCard,
  TriageConsoleCard,
  ChatMemberCleanupCard,
} from "./_blocks";

export default function PlatformSettings() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const BRAND_NAME = branding?.app_name || "Rogue";
  const [gStatus, setGStatus] = useState(null);
  const [params, setParams] = useSearchParams();
  const [banner, setBanner] = useState("");

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "owner") return;
    api.get("/oauth/google/status").then((r) => setGStatus(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const g = params.get("google");
    if (g === "connected") setBanner("Google Calendar connected ✓");
    else if (g === "error" || g === "exchange_failed") setBanner("Google Calendar connection failed.");
    else if (g === "invalid_state") setBanner("Invalid OAuth state — please retry.");
    if (g) {
      const t = setTimeout(() => {
        setBanner(""); params.delete("google"); setParams(params, { replace: true });
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [params, setParams]);

  const connectGoogle = async () => {
    const { data } = await api.get("/oauth/google/login");
    window.location.href = data.authorization_url;
  };
  const disconnectGoogle = async () => {
    await api.post("/oauth/google/disconnect");
    api.get("/oauth/google/status").then((r) => setGStatus(r.data)).catch(() => {});
  };

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  if (!isAdmin) {
    return (
      <EntityShell kindLabel="Settings" title="Platform" backTo="/settings">
        <div className="hv-card p-5 text-sm text-[#a0aabf]" data-testid="platform-settings-forbidden">
          Admin access required.
        </div>
      </EntityShell>
    );
  }

  return (
    <EntityShell kindLabel="Settings" title="Platform" backTo="/settings">
      <div className="space-y-4" data-testid="settings-platform-page">
        {banner && (
          <div className={`hv-card p-3 text-sm ${
            banner.includes("✓") ? "border-emerald-500/40 text-emerald-300" : "border-rose-500/40 text-rose-300"
          }`}>{banner}</div>
        )}

        {/* Google Calendar */}
        {user?.role === "admin" && (
          <div className="hv-card p-5" data-testid="google-calendar-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-[#1fb6c1]" />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold">Google Calendar</h2>
                  <p className="text-sm text-[#a0aabf] max-w-lg mt-1">
                    Connect your Google account so {BRAND_NAME} pushes scheduled job cards and meetings into your personal Calendar.
                  </p>
                  {gStatus?.connected && (
                    <div className="text-xs text-emerald-300 mt-2 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Connected as {gStatus.google_email}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {gStatus?.connected ? (
                  <button onClick={disconnectGoogle} className="hv-btn hv-btn-ghost" data-testid="google-disconnect-btn">
                    <X className="w-4 h-4" /> Disconnect
                  </button>
                ) : (
                  <button onClick={connectGoogle} className="hv-btn hv-btn-primary" data-testid="google-connect-btn">
                    <CalendarCheck className="w-4 h-4" /> Connect Google Calendar
                  </button>
                )}
              </div>
            </div>
            {!gStatus?.connected && (
              <details className="mt-3 text-[11px] text-[#a0aabf]" data-testid="google-help">
                <summary className="cursor-pointer hover:text-white inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-400" /> Getting "restricted by organisation"?
                </summary>
                <div className="mt-2 pl-4 leading-relaxed border-l border-amber-500/30 space-y-2">
                  <p>This is a Google Workspace policy — your IT admin must whitelist {BRAND_NAME} in their tenant.</p>
                </div>
              </details>
            )}
          </div>
        )}

        <BulletinManagerCard />
        <RogueModulesCard />
        <DataMigrationBlock />
        <IndustryTaxonomyCard />
        <DocQACard />

        {/* Data quality */}
        <div className="hv-card p-5" data-testid="data-quality-dashboard">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center">
              <Activity className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-2xl font-bold">Data quality scorecard</h2>
              <p className="text-xs text-[#a0aabf]">
                Quick view of incomplete records across customers, jobcards, equipment, inventory and orders.
              </p>
            </div>
          </div>
          <UnresolvedWidget />
        </div>

        {user?.role === "owner" && (
          <>
            <FeatureFlagsCard />
            <TriageConsoleCard />
            <ChatMemberCleanupCard />
            <TenantDataCleanupCard />
            <GalleryReconcileCard />
          </>
        )}
      </div>
    </EntityShell>
  );
}
